import styled from 'styled-components';
import FieldItem from './FieldItem';
import Text from 'components/Text';
import { Form, FormProps } from 'antd';
import { REPORT_SECTIONS } from '../constants';
import useGetTemplatePrintPayroll from 'features/settingService/services/hooks/useGetTemplatePrintPayroll';
import { useEffect, useMemo, useState } from 'react';
import Button from 'components/Button';
import Box from 'components/Box';
import MyRadio from '../../PaymentTabMenu/components/MyRadio';
import { getMessageError, IResponseDataBody } from 'services/response';
import settingApis from 'features/settingService/services/apis';
import { toast } from 'react-toastify';
import { useAppDispatch } from 'store/hooks';
import shopActions from 'services/shop/actions';
import { EHideFieldReport } from 'utils/hide-field-report';

const defaultValues = REPORT_SECTIONS.reduce(
  (acc, item) => {
    acc[item.key] = true;
    return acc;
  },
  {} as Record<string, boolean>,
);

const payrollReceiptTypes = [
  { value: 'DEFAULT', label: 'Default' },
  { value: 'SIMPLE_PAYROLL', label: 'Simple' },
];

interface IPayrollReceiptFormValues {
  templateName: string;
  [key: string]: string | boolean;
}

const PayrollReceipt = () => {
  const [form] = Form.useForm<IPayrollReceiptFormValues>();
  const values = Form.useWatch([], form);
  const templateName = Form.useWatch('templateName', form);
  const showPercentage = Form.useWatch(EHideFieldReport.SHOW_PERCENTAGE, form);
  const { data, mutate } = useGetTemplatePrintPayroll();
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!data) return;

    const formValues: IPayrollReceiptFormValues = {
      templateName: data.templateName,
    };

    REPORT_SECTIONS.forEach((item) => {
      formValues[item.key] = !data.hideFields?.includes(item.key);
    });

    form.setFieldsValue(formValues);
  }, [data, form]);

  const FILTERED_SECTIONS = useMemo(() => {
    if (templateName === 'SIMPLE_PAYROLL') {
      return REPORT_SECTIONS.slice(0, 9);
    }
    return REPORT_SECTIONS;
  }, [templateName]);

  const handleSubmit = async (values: IPayrollReceiptFormValues) => {
    setLoading(true);
    try {
      const payload = {
        templateName: values.templateName,
        hideFields: Object.keys(values).filter((key) => key !== 'templateName' && !values[key]),
      };

      const res: IResponseDataBody = await settingApis.updateTemplatePrintPayroll(payload);

      if (res?.data?.data) {
        toast.success('Payroll receipt template updated successfully');
        mutate();
        dispatch(shopActions.get.allSetting.fetch());
      }
    } catch (error) {
      toast.error(getMessageError(error) || 'Failed to update payroll receipt template');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    if (data) {
      const formValues: IPayrollReceiptFormValues = {
        templateName: data.templateName,
      };
      REPORT_SECTIONS.forEach((item) => {
        formValues[item.key] = !data.hideFields?.includes(item.key);
      });
      form.setFieldsValue(formValues);
    }
  };

  console.log('values', values);

  return (
    <StyledForm form={form} initialValues={defaultValues} layout='vertical' onFinish={handleSubmit}>
      <Wrapper>
        <Form.Item noStyle name='templateName'>
          <PayrollReceiptType />
        </Form.Item>
        <Container>
          <LeftPanel>
            <Text variant='H5'>Fields</Text>
            {FILTERED_SECTIONS.map((section) => (
              <FieldItem
                key={section.key}
                name={section.key}
                label={section.label}
                description={section.description}
                icon={section.icon}
                bgColor={section.bgColor}
              />
            ))}
          </LeftPanel>

          <RightPanel>
            <ContentPreview>
              {FILTERED_SECTIONS.filter((section) => values?.[section.key]).map((section) => (
                <ContentSection key={section.key} display='flex' flexDirection='column' style={{ gap: 4 }}>
                  {section.isHeader ? (
                    <PreviewSectionTitle className='text-header'>
                      <Text variant='H7' color='text_1'>
                        {section.label.toUpperCase()}
                      </Text>
                      {!section.isPaymentType && (
                        <Text variant='H7' color='text_1'>
                          $0.00
                        </Text>
                      )}
                    </PreviewSectionTitle>
                  ) : (
                    section.labelPreview && (
                      <PreviewRow className={section.isHiddenBorder ? 'hidden-border' : ''}>
                        <Text variant='H9'>
                          {showPercentage && section.labelPreviewPercentage
                            ? section.labelPreviewPercentage
                            : section.labelPreview}
                        </Text>
                        <Text variant='H9'>$0.00</Text>
                      </PreviewRow>
                    )
                  )}

                  {section?.rows?.map((row) => (
                    <PreviewRow
                      key={row}
                      className={
                        section.isHiddenBorder && (section.rows?.indexOf(row) ?? -1) === (section.rows?.length ?? 0) - 1
                          ? 'hidden-border'
                          : ''
                      }
                    >
                      <Text variant='H9'>{row}</Text>
                      <Text variant='H9'>$0.00</Text>
                    </PreviewRow>
                  ))}
                </ContentSection>
              ))}
            </ContentPreview>
          </RightPanel>
        </Container>
      </Wrapper>
      <BottomAction>
        <Button ntype='SECONDARY' cardCashier htmlType='button' width='10rem' onClick={handleReset} disabled={loading}>
          Reset
        </Button>
        <Button ntype='PRIMARY' cardCashier htmlType='submit' width='10rem' disabled={loading}>
          Save
        </Button>
      </BottomAction>
    </StyledForm>
  );
};

export default PayrollReceipt;

const StyledForm = styled(Form)<FormProps<IPayrollReceiptFormValues>>`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const Wrapper = styled.div`
  width: 100%;
  height: calc(100% - 100px);
  padding: 16px 32px;
  box-sizing: border-box;
`;

const Container = styled.div`
  width: 100%;
  height: calc(100% - 45px);
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 16px;
  font-family: Poppins;
  margin-top: 16px;
`;

const LeftPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  grid-column: span 8;
  background: #f6f7fc;
  border-radius: 8px;
  height: calc(100% - 50px);
  overflow-y: auto;
`;

const RightPanel = styled.div`
  background: #f6f7fc;
  border-radius: 8px;
  padding: 16px;
  grid-column: span 4;
  overflow-y: auto;
  height: calc(100% - 50px);
`;

const ContentPreview = styled.div`
  background: #fff;
  box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.25);
  padding: 16px 8px 8px 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PreviewSectionTitle = styled.div`
  background: #233142;
  font-weight: 700;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PreviewRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding-bottom: 4px;
  height: 30px;
  border-bottom: 1px dashed #232f3e;

  &.hidden-border {
    border-bottom: none;
  }
`;

const ContentSection = styled(Box)`
  &:last-child ${PreviewRow} {
    border-bottom: none;
  }
`;

const BottomAction = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 10;
  padding: 16px 24px;
  display: flex;
  justify-content: flex-end;
  gap: 24px;
  background: #f1f5f9;
  box-shadow: 0 -4px 8px 0 rgba(0, 0, 0, 0.15);

  .CONTENT_2 {
    font-size: 20px;
    font-weight: 600;
    font-family: Poppins;
  }
`;

type OtherPaymentTypesProps = {
  value?: string;
  onChange?: (value: string) => void;
};

const PayrollReceiptType = ({ value, onChange = () => undefined }: OtherPaymentTypesProps) => {
  return (
    <Box display='flex' gap='8'>
      {payrollReceiptTypes.map((opt) => (
        <MyRadio key={opt.value} value={value === opt.value} label={opt.label} onChange={() => onChange(opt.value)} />
      ))}
    </Box>
  );
};
